/**
 * Feature-level helpers that ride the shared api client: settings and context
 * usage. Kept out of api.ts so the client stays the client.
 */

import { logger } from '@/utils/logger';

import { api } from './api';

// ---------------------------------------------------------------------------
// Domain-specific helpers (settings)
// ---------------------------------------------------------------------------

interface Setting {
  key: string;
  value: string;
  description?: string;
}

// Get all settings
/**
 * Fetches all application settings from the API.
 *
 * @returns An array of setting objects with key, value, and optional description.
 * @throws Error if the API request fails.
 */
export const getAllSettings = async (): Promise<Setting[]> => {
  try {
    const response = await api.get<{ success: boolean; data: Setting[] }>('/api/settings');

    // Extract the actual settings array from the wrapped response
    return response.data.data || [];
  } catch (error) {
    logger.error('Failed to get settings:', error);

    throw error;
  }
};

// Update a setting
/**
 * Updates a single application setting via the API.
 *
 * @param key - The setting key to update.
 * @param value - The new value for the setting.
 * @param description - Optional description for the setting.
 * @returns The updated setting object.
 * @throws Error if the API request fails.
 */
export const updateSetting = async (
  key: string,
  value: string,
  description: string | null = null
): Promise<Setting> => {
  try {
    const response = await api.post<Setting>('/api/settings/update', {
      key,
      value,
      description,
    });
    return response.data;
  } catch (error) {
    logger.error(`Failed to update setting ${key}:`, error);

    throw error;
  }
};

// Batch update settings
/**
 * Batch-updates multiple application settings in a single API call.
 *
 * @param settings - Array of setting updates with key, value, and optional description.
 * @returns An array of updated setting objects.
 * @throws Error if the API request fails.
 */
export const batchUpdateSettings = async (
  settings: { key: string; value: string; description?: string | null }[]
): Promise<Setting[]> => {
  try {
    const response = await api.put<{ success: boolean; data: Setting[] }>(
      '/api/settings/batch-update',
      { settings }
    );
    return response.data.data || [];
  } catch (error) {
    logger.error('Failed to batch update settings:', error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Domain-specific helpers (conversation context-usage meter)
// ---------------------------------------------------------------------------

/** Per-conversation context-budget consumption for the chat token meter. */
export interface ContextUsage {
  usedTokens: number;
  budgetTokens: number;
  modelContextWindow: number | null;
  pct: number;
}

interface ContextUsageResponse {
  success: boolean;
  data: ContextUsage;
  message: string;
}

/**
 * Fetches the context-budget usage snapshot for a conversation (chat meter).
 *
 * @param conversationId - The conversation whose usage to retrieve.
 * @returns The unwrapped context-usage payload.
 * @throws Error if the API request fails.
 */
export const getContextUsage = async (conversationId: string): Promise<ContextUsage> => {
  const response = await api.get<ContextUsageResponse>(
    `/api/conversations/${conversationId}/context-usage`
  );
  return response.data.data;
};

/** Result of compacting a conversation into a fresh summarized thread. */
export interface CompactResult {
  newConversationId: string;
  chatType: string;
  title: string | null;
}

interface CompactResultResponse {
  success: boolean;
  data: CompactResult;
  message: string;
}

/**
 * Compacts a conversation: summarizes it and spawns a new conversation seeded
 * with that summary, so the thread can continue with a smaller context budget.
 *
 * @param conversationId - The conversation to compact.
 * @returns The unwrapped descriptor of the newly created conversation.
 * @throws Error if the API request fails.
 */
export const compactConversation = async (
  conversationId: string
): Promise<CompactResult> => {
  const response = await api.post<CompactResultResponse>(
    `/api/conversations/${conversationId}/compact`,
    {}
  );
  return response.data.data;
};
